import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { LogOut, TrendingUp, Calendar, Shield, BarChart3, Plus, Brain, Sparkles, Loader2, Trash2, Activity, Target, Upload, RefreshCw, Power, Wallet } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Mt5AccountManager from "@/components/Mt5AccountManager";
import PerformanceReports from "@/components/PerformanceReports";
import { planLabel, normalizePlan } from "@/lib/plans";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const aiMarkdownComponents = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-white mt-6 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-white mt-5 mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-[#007AFF] mt-4 mb-1">{children}</h3>,
  p: ({ children }) => <p className="text-[#A1A1AA] text-sm leading-relaxed mb-2">{children}</p>,
  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
  em: ({ children }) => <em className="text-[#007AFF] italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc list-inside space-y-1 mb-3 text-[#A1A1AA] text-sm">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 mb-3 text-[#A1A1AA] text-sm">{children}</ol>,
  li: ({ children }) => <li className="text-[#A1A1AA] leading-relaxed">{children}</li>,
  hr: () => <hr className="border-[#27272A] my-4" />,
  code: ({ children }) => <code className="bg-[#1C1C1E] text-[#00E676] px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-[#007AFF] pl-4 italic text-[#71717A] my-3">{children}</blockquote>,
};

// Added few checks

export default function DashboardEnhanced() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [trades, setTrades] = useState([]);
  const [propAccounts, setPropAccounts] = useState([]);
  const [aiAnalyses, setAiAnalyses] = useState([]);
  const [showTradeForm, setShowTradeForm] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvResult, setCsvResult] = useState(null);

  const [mt5Accounts, setMt5Accounts] = useState([]);
  const [accountTrades, setAccountTrades] = useState([]);
  const [accountTradesSummary, setAccountTradesSummary] = useState(null);
  const [journalPeriod, setJournalPeriod] = useState('all');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  const [historySyncing, setHistorySyncing] = useState(false);
  const [performanceReport, setPerformanceReport] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);

  const [tradeForm, setTradeForm] = useState({
    pair: "", direction: "buy", entry_price: "", exit_price: "",
    stop_loss: "", take_profit: "", notes: "",
    date: new Date().toISOString().split('T')[0], status: "open"
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) { navigate('/'); return; }
    setUser(JSON.parse(userData));
    refreshUser();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const refreshUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: getHeaders() });
      const updated = {
        ...JSON.parse(localStorage.getItem('user') || '{}'),
        ...res.data,
        subscription_plan: normalizePlan(res.data.subscription_plan),
      };
      localStorage.setItem('user', JSON.stringify(updated));
      setUser(updated);
    } catch (e) { /* token refresh handled elsewhere */ }
  };


  const verifiedMt5Accounts = mt5Accounts.filter((a) => a.verification_status === 'verified');

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchData = async () => {
    const headers = getHeaders();
    try {
      const [statsRes, tradesRes, propRes, aiRes] = await Promise.all([
        axios.get(`${API}/user/stats`, { headers }),
        axios.get(`${API}/user/trades`, { headers }),
        axios.get(`${API}/user/prop-accounts`, { headers }),
        axios.get(`${API}/user/ai-analyses`, { headers })
      ]);
      setStats(statsRes.data);
      setTrades(tradesRes.data);
      setPropAccounts(propRes.data);
      setAiAnalyses(aiRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data');
    }

    try {
      const acctRes = await axios.get(`${API}/user/mt5-accounts`, { headers });
      setMt5Accounts(acctRes.data.accounts || []);
    } catch (e) {
      console.error('Failed to fetch MT5 accounts');
    }

    fetchAccountTrades();
    fetchPerformanceReport();
  };

  const fetchAccountTrades = async (period, startDate, endDate) => {
    try {
      const headers = getHeaders();
      const p = period || journalPeriod;
      let url = `${API}/user/account-trades?period=${p}`;
      if (p === 'custom' && startDate) url += `&start_date=${startDate}`;
      if (p === 'custom' && endDate) url += `&end_date=${endDate}`;
      const [tradesRes, summaryRes] = await Promise.all([
        axios.get(url, { headers }),
        axios.get(`${API}/user/account-trades/summary?period=${p}`, { headers }),
      ]);
      setAccountTrades(tradesRes.data.trades || []);
      setAccountTradesSummary(summaryRes.data);
    } catch (e) { /* empty */ }
  };

  const fetchPerformanceReport = async () => {
    setPerformanceLoading(true);
    try {
      const res = await axios.get(`${API}/user/performance-report`, { headers: getHeaders() });
      setPerformanceReport(res.data);
    } catch (e) {
      setPerformanceReport({ ok: false, message: "Could not load performance report." });
    } finally {
      setPerformanceLoading(false);
    }
  };

  const handleAddTrade = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/user/trades`, {
        ...tradeForm,
        entry_price: parseFloat(tradeForm.entry_price),
        exit_price: tradeForm.exit_price ? parseFloat(tradeForm.exit_price) : null,
        stop_loss: tradeForm.stop_loss ? parseFloat(tradeForm.stop_loss) : null,
        take_profit: tradeForm.take_profit ? parseFloat(tradeForm.take_profit) : null,
        date: new Date(tradeForm.date).toISOString()
      }, { headers: getHeaders() });
      toast.success('Trade added!');
      setShowTradeForm(false);
      setTradeForm({ pair: "", direction: "buy", entry_price: "", exit_price: "", stop_loss: "", take_profit: "", notes: "", date: new Date().toISOString().split('T')[0], status: "open" });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add trade'); }
  };

  const handleDeleteTrade = async (tradeId) => {
    try {
      await axios.delete(`${API}/user/trades/${tradeId}`, { headers: getHeaders() });
      toast.success('Trade deleted');
      fetchData();
    } catch (error) { toast.error('Failed to delete trade'); }
  };

  const handleAIAnalysis = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/user/ai-analysis`, {}, { headers: getHeaders() });
      toast.success('AI analysis complete!');
      setAiAnalyses([res.data, ...aiAnalyses]);
    } catch (error) { toast.error(error.response?.data?.detail || 'AI analysis failed'); }
    finally { setAiLoading(false); }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) { toast.error('Please select a CSV file'); return; }
    setCsvUploading(true); setCsvResult(null);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const res = await axios.post(`${API}/user/trades/upload-csv`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setCsvResult(res.data);
      if (res.data.imported > 0) { toast.success(res.data.message); fetchData(); }
      if (res.data.errors?.length > 0) toast.warning(`${res.data.errors.length} row(s) had errors`);
    } catch (error) { toast.error(error.response?.data?.detail || 'CSV upload failed'); }
    finally { setCsvUploading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const pollPerformanceReport = async (attempts = 12, intervalMs = 5000) => {
    setPerformanceLoading(true);
    for (let i = 0; i < attempts; i += 1) {
      try {
        const res = await axios.get(`${API}/user/performance-report`, { headers: getHeaders() });
        setPerformanceReport(res.data);
        if (!res.data?.pending) {
          if (res.data?.ok) toast.success('Performance report updated.');
          setPerformanceLoading(false);
          return;
        }
      } catch (e) {
        setPerformanceReport({ ok: false, message: "Could not load performance report." });
        setPerformanceLoading(false);
        return;
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    setPerformanceLoading(false);
    toast.info('Sync still running. Refresh charts in a moment.');
  };

  const handleHistorySync = async () => {
    setHistorySyncing(true);
    try {
      await axios.post(`${API}/user/account-trades/sync`, {}, { headers: getHeaders() });
      toast.success('History sync requested. Building report on executor…');
      setPerformanceReport({ ok: false, pending: true, message: 'History sync in progress…' });
      pollPerformanceReport();
      setTimeout(fetchData, 5000);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to request sync'); }
    finally { setHistorySyncing(false); }
  };

  const handleJournalPeriodChange = (p) => {
    setJournalPeriod(p);
    if (p !== 'custom') fetchAccountTrades(p);
  };

  const closedTrades = trades.filter(t => t.status === 'closed');

  const journalTradesWithPnl = accountTrades.filter((t) => t.profit_usd != null);
  const analyzableTradeCount = journalTradesWithPnl.length > 0
    ? journalTradesWithPnl.length
    : closedTrades.filter((t) => t.profit_usd != null).length;

  const recentTrades = useMemo(() => {
    const source = accountTrades.length > 0 ? accountTrades : trades;
    return [...source]
      .sort((a, b) => new Date(b.opened_at || b.date) - new Date(a.opened_at || a.date))
      .slice(0, 5);
  }, [accountTrades, trades]);


  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />

      <div className="pt-20 md:pt-24 pb-24 md:pb-12 px-3 sm:px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-3xl font-bold truncate">Welcome, {user.name}!</h1>
              <p className="text-xs sm:text-sm text-[#A1A1AA] mt-0.5">Your trading dashboard</p>
            </div>
            <Button onClick={handleLogout} variant="ghost" size="sm" className="text-[#A1A1AA] hover:text-[#FF3D00] shrink-0">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline ml-1.5">Logout</span>
            </Button>
          </div>

          <Tabs defaultValue="overview" className="space-y-4 sm:space-y-6">
            <div className="overflow-x-auto scrollbar-hide -mx-3 px-3 sm:-mx-0 sm:px-0">
              <TabsList className="bg-[#111111] border border-[#27272A] h-10 p-1">
                <TabsTrigger value="overview" className="text-xs sm:text-sm px-2.5 sm:px-4" data-testid="tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="trades" className="text-xs sm:text-sm px-2.5 sm:px-4" data-testid="tab-trades">Trades</TabsTrigger>
                <TabsTrigger value="ai-analysis" className="text-xs sm:text-sm px-2.5 sm:px-4" data-testid="tab-ai">AI</TabsTrigger>
                <TabsTrigger value="performance" className="text-xs sm:text-sm px-2.5 sm:px-4" data-testid="tab-performance">Charts</TabsTrigger>
                <TabsTrigger value="account" className="text-xs sm:text-sm px-2.5 sm:px-4" data-testid="tab-account">Account</TabsTrigger>
              </TabsList>
            </div>

            {/* ═══ OVERVIEW ═══ */}
            <TabsContent value="overview" className="space-y-4 sm:space-y-6">
              {stats && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { label: 'Total Profit', value: `$${stats.total_profit.toLocaleString()}`, icon: <TrendingUp className="w-4 h-4" />, color: 'text-[#00E676]' },
                    { label: 'Win Rate', value: `${stats.win_rate}%`, icon: <BarChart3 className="w-4 h-4" />, color: 'text-[#007AFF]' },
                    { label: 'Total Trades', value: stats.trades_count, icon: <Activity className="w-4 h-4" />, color: 'text-[#FFC107]' },
                    { label: 'Total Pips', value: stats.total_pips.toLocaleString(), icon: <Target className="w-4 h-4" />, color: 'text-[#00E676]' },
                  ].map((s, i) => (
                    <div key={i} className="bg-[#111111] border border-[#27272A] rounded-xl p-3 sm:p-5">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className={s.color}>{s.icon}</span>
                        <p className="text-[10px] sm:text-xs text-[#A1A1AA]">{s.label}</p>
                      </div>
                      <p className={`text-lg sm:text-2xl font-bold font-data ${s.color}`}>{s.value}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* MT5 status banner */}
              {verifiedMt5Accounts.length > 0 && (
                <div className="space-y-2">
                  {verifiedMt5Accounts.map((acct) => (
                    <div key={acct.id} className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border ${acct.trading_enabled ? 'bg-[#00E676]/5 border-[#00E676]/30' : 'bg-[#111111] border-[#27272A]'}`}>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Power className={`w-4 h-4 shrink-0 ${acct.trading_enabled ? 'text-[#00E676]' : 'text-[#52525B]'}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{acct.trading_enabled ? 'Auto-Trading Active' : 'Auto-Trading Paused'}</p>
                          <p className="text-[10px] text-[#A1A1AA]">Account {acct.account_number} · ${Number(acct.allocated_capital || 0).toLocaleString()} allocated</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Quick Actions */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                <Button onClick={() => setShowTradeForm(true)} className="bg-[#00E676] hover:bg-[#00C853] text-black h-12 sm:h-16 text-xs sm:text-sm font-semibold">
                  <Plus className="w-4 h-4 mr-1.5" /> Add Trade
                </Button>
                <Button onClick={() => setShowCsvUpload(true)} className="bg-[#9C27B0] hover:bg-[#7B1FA2] text-white h-12 sm:h-16 text-xs sm:text-sm font-semibold">
                  <Upload className="w-4 h-4 mr-1.5" /> Upload CSV
                </Button>
                <Button onClick={handleAIAnalysis} disabled={aiLoading || analyzableTradeCount === 0} className="bg-[#007AFF] hover:bg-[#0062CC] text-white h-12 sm:h-16 text-xs sm:text-sm font-semibold">
                  {aiLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Brain className="w-4 h-4 mr-1.5" />}
                  {aiLoading ? 'Analyzing...' : 'AI Analysis'}
                </Button>
                <Button onClick={() => navigate('/prop-firm')} className="bg-[#FFC107] hover:bg-[#FFB300] text-black h-12 sm:h-16 text-xs sm:text-sm font-semibold">
                  <Shield className="w-4 h-4 mr-1.5" /> Get Funded
                </Button>
              </div>

              {/* Recent Trades */}
              <Card className="bg-[#111111] border-[#27272A]">
                <CardHeader className="pb-2 px-3 sm:px-6 pt-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                      <Activity className="w-4 h-4 text-[#00E676]" /> Recent Trades
                    </CardTitle>
                    {recentTrades.length > 0 && (
                      <Button variant="ghost" size="sm" className="text-[#A1A1AA] hover:text-[#00E676] text-xs h-7 px-2"
                        onClick={() => document.querySelector('[data-testid="tab-trades"]')?.click()}>
                        View All
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="px-3 sm:px-6 pb-4">
                  {recentTrades.length === 0 ? (
                    <p className="text-center py-6 text-sm text-[#52525B]">No trades yet</p>
                  ) : (
                    <div className="overflow-x-auto -mx-3 sm:mx-0">
                      <table className="w-full min-w-[500px]">
                        <thead>
                          <tr className="border-b border-[#27272A]">
                            {['Date','Pair','Dir','Entry','P&L','Status'].map(h => (
                              <th key={h} className="text-left py-2 px-2 text-[10px] font-medium text-[#52525B] uppercase tracking-wider">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {recentTrades.map((t) => (
                            <tr key={t.id} className="border-b border-[#27272A]/30">
                              <td className="py-2 px-2 text-[11px] text-[#A1A1AA]">{new Date(t.opened_at || t.date).toLocaleDateString()}</td>
                              <td className="py-2 px-2 text-[11px] font-medium">{t.pair}</td>
                              <td className="py-2 px-2">
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${(t.direction || '').toLowerCase() === 'buy' ? 'bg-[#00E676]/10 text-[#00E676]' : 'bg-[#FF3D00]/10 text-[#FF3D00]'}`}>
                                  {(t.direction || '').toUpperCase()}
                                </span>
                              </td>
                              <td className="py-2 px-2 text-[11px] font-data">{t.entry_price != null ? Number(t.entry_price).toFixed(5) : '-'}</td>
                              <td className={`py-2 px-2 text-[11px] font-data font-bold ${(t.profit_usd || 0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3D00]'}`}>
                                {t.profit_usd != null ? `$${Number(t.profit_usd).toFixed(2)}` : '-'}
                              </td>
                              <td className="py-2 px-2">
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${t.status === 'open' ? 'bg-[#FFC107]/10 text-[#FFC107]' : 'bg-[#27272A] text-[#71717A]'}`}>
                                  {(t.status || 'closed').toUpperCase()}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ═══ TRADES / JOURNAL ═══ */}
            <TabsContent value="trades" className="space-y-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h2 className="text-lg sm:text-2xl font-bold">Trade Journal</h2>
                <div className="flex gap-2">
                  {verifiedMt5Accounts.length > 0 && (
                    <Button onClick={handleHistorySync} disabled={historySyncing} variant="outline" size="sm" className="border-[#007AFF]/50 text-[#007AFF] hover:bg-[#007AFF]/10 h-8 text-xs">
                      {historySyncing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Sync
                    </Button>
                  )}
                  <Button onClick={() => setShowCsvUpload(true)} variant="outline" size="sm" className="border-[#9C27B0]/50 text-[#CE93D8] hover:bg-[#9C27B0]/10 h-8 text-xs">
                    <Upload className="w-3 h-3 mr-1" /> CSV
                  </Button>
                  <Button onClick={() => setShowTradeForm(true)} size="sm" className="bg-[#00E676] hover:bg-[#00C853] text-black h-8 text-xs">
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
              </div>

              {/* Period pills */}
              <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
                {['all','today','week','month','year','custom'].map(p => (
                  <button key={p} onClick={() => handleJournalPeriodChange(p)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all ${journalPeriod === p ? 'bg-[#00E676] text-black' : 'bg-[#1C1C1E] text-[#A1A1AA] hover:bg-[#27272A]'}`}>
                    {p === 'all' ? 'All' : p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
              {journalPeriod === 'custom' && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Input type="date" value={customDateStart} onChange={e => setCustomDateStart(e.target.value)} className="bg-[#1C1C1E] border-[#27272A] text-white h-8 w-32 text-xs" />
                  <span className="text-[#52525B] text-xs">to</span>
                  <Input type="date" value={customDateEnd} onChange={e => setCustomDateEnd(e.target.value)} className="bg-[#1C1C1E] border-[#27272A] text-white h-8 w-32 text-xs" />
                  <Button size="sm" onClick={() => { if (customDateStart) fetchAccountTrades('custom', customDateStart, customDateEnd); }} className="bg-[#007AFF] text-white h-8 text-xs px-3">Apply</Button>
                </div>
              )}

              {/* Summary */}
              {accountTradesSummary && (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {[
                    { label: 'P&L', value: `$${accountTradesSummary.total_pnl?.toFixed(2)}`, color: (accountTradesSummary.total_pnl || 0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3D00]' },
                    { label: 'Win Rate', value: `${accountTradesSummary.win_rate}%`, color: 'text-[#007AFF]' },
                    { label: 'Trades', value: accountTradesSummary.total_trades, color: 'text-[#FFC107]' },
                    { label: 'Best', value: `$${accountTradesSummary.best_trade?.toFixed(2)}`, color: 'text-[#00E676]' },
                    { label: 'Worst', value: `$${accountTradesSummary.worst_trade?.toFixed(2)}`, color: 'text-[#FF3D00]' },
                  ].map((s, i) => (
                    <div key={i} className="bg-[#111111] border border-[#27272A] rounded-lg p-2.5 sm:p-3">
                      <p className="text-[9px] sm:text-[10px] text-[#52525B] uppercase tracking-wider">{s.label}</p>
                      <p className={`text-sm sm:text-lg font-bold font-data ${s.color}`}>{s.value}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Table */}
              {accountTrades.length > 0 ? (
                <div className="bg-[#111111] border border-[#27272A] rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                      <thead className="bg-[#0A0A0A]">
                        <tr>{['Date','Pair','Dir','Lot','Entry','Exit','P&L','Status'].map(h => (
                          <th key={h} className="text-left py-2.5 px-3 text-[10px] font-medium text-[#52525B] uppercase tracking-wider">{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>{accountTrades.map((t) => (
                        <tr key={t.id} className="border-t border-[#27272A]/30 hover:bg-[#111111]">
                          <td className="py-2 px-3 text-[11px] text-[#A1A1AA]">{t.opened_at ? new Date(t.opened_at).toLocaleDateString() : '-'}</td>
                          <td className="py-2 px-3 text-[11px] font-medium">{t.pair}</td>
                          <td className="py-2 px-3"><span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${t.direction === 'buy' ? 'bg-[#00E676]/10 text-[#00E676]' : 'bg-[#FF3D00]/10 text-[#FF3D00]'}`}>{(t.direction||'').toUpperCase()}</span></td>
                          <td className="py-2 px-3 text-[11px] font-data">{t.lot_size}</td>
                          <td className="py-2 px-3 text-[11px] font-data">{t.entry_price ? Number(t.entry_price).toFixed(5) : '-'}</td>
                          <td className="py-2 px-3 text-[11px] font-data">{t.exit_price ? Number(t.exit_price).toFixed(5) : '-'}</td>
                          <td className={`py-2 px-3 text-[11px] font-data font-bold ${(t.profit_usd||0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3D00]'}`}>{t.profit_usd != null ? `$${Number(t.profit_usd).toFixed(2)}` : '-'}</td>
                          <td className="py-2 px-3"><span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                            t.status === 'open' ? 'bg-[#FFC107]/10 text-[#FFC107]' : t.status === 'closed' ? 'bg-[#27272A] text-[#71717A]' : t.status === 'failed' ? 'bg-[#FF3D00]/10 text-[#FF3D00]' : 'bg-yellow-500/10 text-yellow-400'
                          }`}>{(t.status||'').toUpperCase()}</span></td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </div>
              ) : trades.length > 0 ? (
                <div className="bg-[#111111] border border-[#27272A] rounded-xl overflow-hidden">
                  <p className="text-xs text-[#52525B] px-4 py-2 bg-[#0A0A0A] border-b border-[#27272A]">Manual trades — link MT5 for auto-journal</p>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[500px]">
                      <thead><tr>{['Date','Pair','Dir','Entry','Pips','P&L',''].map(h => (
                        <th key={h} className="text-left py-2 px-3 text-[10px] font-medium text-[#52525B] uppercase">{h}</th>
                      ))}</tr></thead>
                      <tbody>{trades.map(t => (
                        <tr key={t.id} className="border-t border-[#27272A]/30">
                          <td className="py-2 px-3 text-[11px] text-[#A1A1AA]">{new Date(t.date).toLocaleDateString()}</td>
                          <td className="py-2 px-3 text-[11px] font-medium">{t.pair}</td>
                          <td className="py-2 px-3"><span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${t.direction.toLowerCase() === 'buy' ? 'bg-[#00E676]/10 text-[#00E676]' : 'bg-[#FF3D00]/10 text-[#FF3D00]'}`}>{t.direction.toUpperCase()}</span></td>
                          <td className="py-2 px-3 text-[11px] font-data">{t.entry_price?.toFixed(5)}</td>
                          <td className={`py-2 px-3 text-[11px] font-data font-bold ${(t.pips||0)>=0 ? 'text-[#00E676]' : 'text-[#FF3D00]'}`}>{t.pips ? `${t.pips>=0?'+':''}${t.pips}` : '-'}</td>
                          <td className={`py-2 px-3 text-[11px] font-data font-bold ${(t.profit_usd||0)>=0 ? 'text-[#00E676]' : 'text-[#FF3D00]'}`}>{t.profit_usd ? `$${t.profit_usd.toFixed(2)}` : '-'}</td>
                          <td className="py-2 px-3"><button onClick={() => handleDeleteTrade(t.id)} className="text-[#52525B] hover:text-[#FF3D00]"><Trash2 className="w-3.5 h-3.5" /></button></td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-sm text-[#52525B]">No trades yet. Link your MT5 account or add trades manually.</div>
              )}
            </TabsContent>

            {/* ═══ AI ═══ */}
            <TabsContent value="ai-analysis" className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-2xl font-bold">AI Analysis</h2>
                <Button onClick={handleAIAnalysis} disabled={aiLoading || analyzableTradeCount === 0} size="sm" className="bg-[#007AFF] hover:bg-[#0062CC] text-white h-8 text-xs">
                  {aiLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  {aiLoading ? 'Analyzing...' : 'New Analysis'}
                </Button>
              </div>
              {aiAnalyses.length === 0 ? (
                <div className="text-center py-12 text-sm text-[#52525B]">Sync your trade journal first, then generate an AI analysis.</div>
              ) : (
                <div className="space-y-3">
                  {aiAnalyses.map(a => (
                    <Card key={a.id} className="bg-[#111111] border-[#007AFF]/20">
                      <CardHeader className="pb-2 px-4 pt-4">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-sm font-semibold"><Brain className="w-4 h-4 text-[#007AFF]" /> AI Report</span>
                          <span className="text-[10px] text-[#52525B]">{new Date(a.created_at).toLocaleDateString()} - {a.trades_analyzed} trades</span>
                        </div>
                      </CardHeader>
                      <CardContent className="px-4 pb-4">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={aiMarkdownComponents}>
                          {a.analysis}
                        </ReactMarkdown>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ═══ CHARTS ═══ */}
            <TabsContent value="performance" className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg sm:text-2xl font-bold">Performance Reports</h2>
                <Button
                  onClick={fetchPerformanceReport}
                  disabled={performanceLoading}
                  variant="outline"
                  size="sm"
                  className="border-[#27272A] text-[#A1A1AA] hover:text-[#00E676] h-8 text-xs shrink-0"
                >
                  {performanceLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  <span className="ml-1.5 hidden sm:inline">Refresh</span>
                </Button>
              </div>
              <PerformanceReports report={performanceReport} loading={performanceLoading} />
            </TabsContent>

            {/* ═══ ACCOUNT ═══ */}
            <TabsContent value="account" className="space-y-4">
              <Mt5AccountManager
                getHeaders={getHeaders}
                onAccountsChange={(accounts) => setMt5Accounts(accounts)}
              />

              {/* Subscription */}
              <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-[#111111] border border-[#27272A]">
                <div className="flex items-center gap-2.5">
                  <Wallet className="w-4 h-4 text-[#00E676]" />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="px-2 py-0.5 bg-[#00E676]/10 text-[#00E676] rounded text-[10px] font-bold uppercase">{planLabel(user.subscription_plan)}</span>
                      <span className="px-2 py-0.5 bg-[#00E676]/10 text-[#00E676] rounded text-[10px] font-bold uppercase">{user.subscription_status}</span>
                    </div>
                    {user.renewal_date && (
                      <p className="text-[10px] text-[#52525B] mt-0.5 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Renews {new Date(user.renewal_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Add Trade Dialog */}
      <Dialog open={showTradeForm} onOpenChange={setShowTradeForm}>
        <DialogContent className="bg-[#111111] border-[#27272A] text-white max-h-[90vh] overflow-y-auto mx-2">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Add Trade</DialogTitle>
            <DialogDescription className="text-[#52525B] text-xs">Log a trade for tracking and AI analysis</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddTrade} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Pair</Label><Input required value={tradeForm.pair} onChange={e => setTradeForm({...tradeForm, pair: e.target.value.toUpperCase()})} className="bg-[#0A0A0A] border-[#27272A] text-white h-9 mt-1" placeholder="EUR/USD" /></div>
              <div><Label className="text-xs">Direction</Label>
                <Select value={tradeForm.direction} onValueChange={v => setTradeForm({...tradeForm, direction: v})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white h-9 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111111] border-[#27272A] text-white"><SelectItem value="buy">BUY</SelectItem><SelectItem value="sell">SELL</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Entry</Label><Input type="number" step="0.00001" required value={tradeForm.entry_price} onChange={e => setTradeForm({...tradeForm, entry_price: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] text-white h-9 mt-1" /></div>
              <div><Label className="text-xs">Exit</Label><Input type="number" step="0.00001" value={tradeForm.exit_price} onChange={e => setTradeForm({...tradeForm, exit_price: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] text-white h-9 mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">SL</Label><Input type="number" step="0.00001" value={tradeForm.stop_loss} onChange={e => setTradeForm({...tradeForm, stop_loss: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] text-white h-9 mt-1" /></div>
              <div><Label className="text-xs">TP</Label><Input type="number" step="0.00001" value={tradeForm.take_profit} onChange={e => setTradeForm({...tradeForm, take_profit: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] text-white h-9 mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Date</Label><Input type="date" required value={tradeForm.date} onChange={e => setTradeForm({...tradeForm, date: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] text-white h-9 mt-1" /></div>
              <div><Label className="text-xs">Status</Label>
                <Select value={tradeForm.status} onValueChange={v => setTradeForm({...tradeForm, status: v})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white h-9 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111111] border-[#27272A] text-white"><SelectItem value="open">OPEN</SelectItem><SelectItem value="closed">CLOSED</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={tradeForm.notes} onChange={e => setTradeForm({...tradeForm, notes: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] text-white mt-1" rows={2} placeholder="Optional..." /></div>
            <Button type="submit" className="w-full bg-[#00E676] hover:bg-[#00C853] text-black h-9 text-xs font-semibold">Add Trade</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={showCsvUpload} onOpenChange={open => { if (!open) { setCsvFile(null); setCsvResult(null); } setShowCsvUpload(open); }}>
        <DialogContent className="bg-[#111111] border-[#27272A] text-white max-h-[90vh] overflow-y-auto mx-2">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2"><Upload className="w-4 h-4 text-[#9C27B0]" /> Upload CSV</DialogTitle>
            <DialogDescription className="text-[#52525B] text-xs">Required: date, pair, direction, entry_price</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <pre className="text-[10px] text-[#52525B] bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3 overflow-x-auto">
{`date,pair,direction,entry_price,exit_price,status
2026-03-15,EUR/USD,buy,1.08500,1.08750,closed`}
            </pre>
            <Input type="file" accept=".csv" onChange={e => { setCsvFile(e.target.files[0] || null); setCsvResult(null); }}
              className="bg-[#0A0A0A] border-[#27272A] text-white text-xs file:bg-[#9C27B0] file:text-white file:border-0 file:rounded file:px-2 file:py-0.5 file:mr-2 file:text-xs" />
            {csvResult && (
              <div className={`p-2.5 rounded-lg text-xs ${csvResult.imported > 0 ? 'bg-[#00E676]/10 text-[#00E676]' : 'bg-[#FF3D00]/10 text-[#FF3D00]'}`}>
                {csvResult.message}
                {csvResult.errors?.length > 0 && <p className="mt-1 text-[10px] text-[#FF8A65]">{csvResult.errors.length} error(s)</p>}
              </div>
            )}
            <Button onClick={handleCsvUpload} disabled={!csvFile || csvUploading} className="w-full bg-[#9C27B0] hover:bg-[#7B1FA2] text-white h-9 text-xs">
              {csvUploading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Uploading...</> : <><Upload className="w-3 h-3 mr-1" /> Upload</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
